import { jsx as _jsx } from "react/jsx-runtime";
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { de, enUS } from 'date-fns/locale';
import { createContext, useMemo } from 'react';
import { useTranslation as reactUseTranslation } from 'react-i18next';
import { apiDateTimeToDate, apiDateToDate, dateToApiDate, dateToApiDateTime, Gender, Locale, RecruiterVoucher, } from '../types';
const initialLocaleContextValue = {
    useTranslation: reactUseTranslation,
    dateFormat: '',
    apiDateToDate: () => new Date(),
    dateToApiDate: () => '',
    apiDateTimeToDate: () => new Date(),
    dateToApiDateTime: () => '',
    utcTimeToLocalTime: () => '',
    formatDate: () => '',
    formatDateTime: () => '',
    formatTime: () => '',
    formatMoneyAmount: () => '',
    formatSalutation: () => '',
    formatVoucher: () => '',
    formatName: () => '',
    formatAddress: () => '',
    formatPhone: () => '',
};
export const timeZoneClient = Intl.DateTimeFormat().resolvedOptions().timeZone;
export const timeZoneServer = 'Europe/Vienna';
export const LocaleContext = createContext(initialLocaleContextValue);
export const LocaleConsumer = LocaleContext.Consumer;
export function LocaleProvider({ locale, useTranslation: customUseTranslation, children, }) {
    const useTranslation = customUseTranslation ?? reactUseTranslation;
    const { t } = useTranslation();
    const dateFnsLocale = useMemo(() => {
        switch (locale) {
            case Locale.DE:
                return de;
            case Locale.EN:
                return enUS;
            default:
                throw new Error(`Locale not implemented: ${locale}`);
        }
    }, [locale]);
    // Shoúld also work see implementation @mui/x-date-pickers/LocalizationProvider
    const utils = useMemo(() => new AdapterDateFns({
        locale: dateFnsLocale,
    }), [dateFnsLocale]);
    const dateFormat = getDateFormat();
    const apiDateToDateApplied = apiDateToDate.bind(null, timeZoneClient, timeZoneServer);
    const dateToApiDateApplied = dateToApiDate.bind(null, timeZoneClient, timeZoneServer);
    const apiDateTimeToDateApplied = apiDateTimeToDate.bind(null, timeZoneClient, timeZoneServer);
    const dateToApiDateTimeApplied = dateToApiDateTime.bind(null, timeZoneClient, timeZoneServer);
    const formatDateApplied = formatDate.bind(null, utils, apiDateToDateApplied);
    const formatDateTimeApplied = formatDateTime.bind(null, utils, apiDateTimeToDateApplied);
    const formatTimeApplied = formatTime.bind(null, utils, apiDateTimeToDateApplied);
    // FIXME: this is a fix until we know how to handle timezones and in which format the backend sends us the dates
    const utcTimeToLocalDateTimeApplied = (apiDateTime) => {
        // convert apiDateTime string to Date
        const datetime = new Date(apiDateTime);
        // get locale timezone offset to UTC in minutes (for Vienna Summer time the result is -120)
        // https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Date/getTimezoneOffset
        const localDateTime = new Date().getTimezoneOffset();
        // apply offset to apiDateTime
        const date = new Date(datetime.getTime() + localDateTime * -60000).toString();
        return `${formatDateApplied(date)}, ${formatTimeApplied(date)}`;
    };
    const formatMoneyAmountApplied = formatMoneyAmount.bind(null, locale);
    const formatSalutationApplied = formatSalutation.bind(null, t);
    const formatVoucherApplied = formatVoucher.bind(null, t);
    const formatNameApplied = formatName.bind(null, t);
    const formatAddressApplied = formatAddress.bind(null, locale);
    const formatPhoneApplied = formatPhone.bind(null, locale);
    return (_jsx(LocaleContext.Provider, { value: {
            useTranslation,
            dateFormat,
            apiDateToDate: apiDateToDateApplied,
            dateToApiDate: dateToApiDateApplied,
            apiDateTimeToDate: apiDateTimeToDateApplied,
            dateToApiDateTime: dateToApiDateTimeApplied,
            utcTimeToLocalTime: utcTimeToLocalDateTimeApplied,
            formatDate: formatDateApplied,
            formatDateTime: formatDateTimeApplied,
            formatTime: formatTimeApplied,
            formatMoneyAmount: formatMoneyAmountApplied,
            formatSalutation: formatSalutationApplied,
            formatVoucher: formatVoucherApplied,
            formatName: formatNameApplied,
            formatAddress: formatAddressApplied,
            formatPhone: formatPhoneApplied,
        }, children: children }));
}
function getDateFormat() {
    // return 'keyboardDate';
    return 'dd.MM.yyyy';
}
const invalidDate = 'Invalid Date';
function formatDate(utils, apiDateToDate, value) {
    const date = apiDateToDate(value);
    if (date.toString() === invalidDate) {
        console.error(`${invalidDate}: ${value}`);
        // this shouldnt happen
        return invalidDate;
    }
    const format = getDateFormat();
    return utils.formatByString(date, format);
}
function formatDateTime(utils, apiDateTimeToDate, value) {
    const date = apiDateTimeToDate(value);
    if (date.toString() === invalidDate) {
        console.error(`${invalidDate}: ${value}`);
        // this shouldnt happen
        return invalidDate;
    }
    const format = getDateFormat();
    return utils.formatByString(date, format);
}
function formatTime(utils, apiDateTimeToDate, value) {
    const date = apiDateTimeToDate(value);
    if (date.toString() === invalidDate) {
        console.error(`${invalidDate}: ${value}`);
        // this shouldnt happen
        return invalidDate;
    }
    /** Not localized full time format string @example "23:44" */
    return utils.format(date, 'fullTime24h');
}
function formatMoneyAmount(locale, value, currency) {
    if (value === undefined || value === null || isNaN(value)) {
        return '--,--';
    }
    return new Intl.NumberFormat(locale, {
        style: 'currency',
        currency: currency ?? 'EUR',
    }).format(value);
}
function formatSalutation(t, value) {
    let result = '';
    const optionsSalutation = Gender.createOptions(t);
    const selectionSalutation = optionsSalutation.find((option) => option.value === value.gender);
    if (selectionSalutation) {
        result += selectionSalutation.label;
    }
    return result;
}
function formatVoucher(t, value) {
    let result = '';
    const optionsVoucher = RecruiterVoucher.createOptions(t);
    const selectionVoucher = optionsVoucher.find((option) => option.value === value);
    if (selectionVoucher) {
        result += selectionVoucher.label;
    }
    return result;
}
function formatName(t, value) {
    return [
        Gender.getSalutation(t, value.gender),
        value.title,
        value.firstName,
        value.lastName,
    ]
        .filter((part) => !!part)
        .join(' ');
}
function formatAddress(locale, value) {
    const result = [];
    let line = '';
    line = '';
    if (value.postalCode) {
        line += value.postalCode;
    }
    if (value.city) {
        line += (!!value.postalCode && ' ') || '';
        line += value.city;
    }
    if (line) {
        result.push(line);
    }
    line = '';
    if (value.streetName) {
        line += value.streetName;
    }
    if (value.houseNo) {
        line += (!!value.streetName && ' ') || '';
        line += value.houseNo;
    }
    if (value.staircase) {
        line += (!!value.houseNo && '/') || (!!value.streetName && ' ') || '';
        line += value.staircase;
    }
    if (value.doorNo) {
        line +=
            ((!!value.houseNo || !!value.staircase) && '/') ||
                (!!value.streetName && ' ') ||
                '';
        line += value.doorNo;
    }
    if (line) {
        result.push(line);
    }
    return result.join('<br>'); // NOTE: requires dangerouslySetInnerHTML
}
function formatPhone(locale, value) {
    let result = '';
    result += value.countryCode;
    result += ' ';
    result += '(0)';
    result += value.providerCode;
    result += ' ';
    result += value.telNr;
    return result;
}
